import React, { PropsWithChildren, useMemo, useCallback } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import './BrandHero.css';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { ButtonWrapper } from '../LinkWrapper/LinkWrapper';
import { Video, Image, Section } from '../../importCL';
import { useWidthContext } from '../../providers/Width';

interface IBrandHeroProps extends MappedComponentProperties {
    fontColor?: 'white' | 'black';
    backgroundVariant?: 'none' | 'colour' | 'image';
    backgroundColor?: 'purple' | 'blue' | 'light-gray' | 'white';
    backgroundImagePath?: string;
    mobileBackgroundImagePath?: string;
    backgroundOverlay?: boolean;
    headline?: string;
    headingAlignment?: 'left' | 'center' | 'right';
    subheading?: string;
    subheadingTag?: 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    subheadingAlignment?: 'left' | 'center' | 'right';
    descriptionAlignment?: 'left' | 'center' | 'right';
    description?: string;
    videoLink?: string;
    imagePath?: string;
    imageAlt?: string;
    mediaColumn?: 'left' | 'right';
    mobileImagePosition?: 'below' | 'between';
    buttonVariant?: 'solid' | 'ghosted-light' | 'ghosted-dark' | 'link' | 'link-light';
    ctaSize?: 'large' | 'medium' | 'small';
    ctaLink?: string;
    ctaText?: string;
    ctaJustify?: 'left' | 'center' | 'right';
}

export const BrandHeroEditConfig = {
    emptyLabel: 'BrandHero',
    isEmpty: (props: PropsWithChildren<IBrandHeroProps>) => {
        return !props || 
            (!props.backgroundImagePath && !props.mobileBackgroundImagePath && !props.headline 
                && !props.subheading && !props.description && !props.videoLink && !props.imagePath 
                && !props.ctaLink && !props.ctaText && props.backgroundVariant!=='colour');
    },
};

const COLOR_CLASSES = {
    background: {
        purple: 'BrandHero--bg-purple',
        blue: 'BrandHero--bg-blue',
        'light-gray': 'BrandHero--bg-light-gray',
        white: 'BrandHero--bg-white',
    },
    font: {
        white: 'BrandHero--font-white',
        black: 'BrandHero--font-black',
    },
} as const;

const alignment = {
    left: 'text-align-left',
    center: 'text-align-center',
    right: 'text-align-right',
    undefined: '',
};

const BrandHero = (props: PropsWithChildren<IBrandHeroProps>): JSX.Element => {

    const { width } = useWidthContext();
    const hasImage = props.imagePath && props.imagePath.trim() !== '';
    const hasVideo = props.videoLink && props.videoLink.trim() !== '';

    const outerContainerProperties = useMemo(() => {
        const toReturn: {[key: string]: any} = { style: {} };
    
        toReturn.className = 'BrandHero BrandHero__Section';

        if (props.backgroundVariant === 'colour') {
            toReturn.className += ` ${COLOR_CLASSES.background[props.backgroundColor]}`;
        } else if (width < 768 && props.mobileBackgroundImagePath) {
            toReturn.bgImg = props.mobileBackgroundImagePath;
            toReturn.bgOverlay = props.backgroundOverlay;
        } else {
            toReturn.bgImg = props.backgroundImagePath;
            toReturn.bgOverlay = props.backgroundOverlay;
        }
    
        return toReturn;
    }, [
        props, width,
    ]);

    const getContentClasses = (): string => {
        if (!hasImage && !hasVideo) {
            return 'BrandHero__content--media-none';
        }
        const classes = ['BrandHero__content'];

        if (props.mediaColumn === 'left') {
            classes.push('BrandHero__content--media-left');
        } else if (props.mediaColumn === 'right') {
            classes.push('BrandHero__content--media-right');
        }

        const mobileClass = props.mobileImagePosition === 'below'
            ? 'BrandHero__content--media-below'
            : 'BrandHero__content--media-between';
        classes.push(mobileClass);

        return classes.join(' ');
    };

    const getTextClasses = (): string => {
        return props.fontColor && COLOR_CLASSES.font[props.fontColor]
            ? COLOR_CLASSES.font[props.fontColor]
            : '';
    };

    const renderMedia = () => {
        if (hasVideo) {
            return (
                <Video 
                    youtubeEmbedURL={props.videoLink}
                    title={props.headline ? props.headline : 'Youtube Video'}
                    videoDisclaimer={(<p className={`ytdisclaimer text-xs mt-6 ${textClasses}`.trim()}>Credit One Bank uses YouTube API services and by watching the video, you are agreeing to the YouTube Terms of Service (<a href='https://youtube.com/terms'>https://youtube.com/terms</a>) and Google Privacy Policy (<a href='https://www.google.com/policies/privacy'>https://www.google.com/policies/privacy</a>).</p>)}
                />                
            );
        }
        if (hasImage) {
            return (
                <Image 
                    src={props.imagePath} 
                    alt={props.imageAlt || 'BrandHero image'}
                />
            );
        }
    };

    const renderCTA = () => {
        if (!props.ctaLink || !props.ctaText) return null;

        return (
            <div className='BrandHero__cta-wrapper'>
                <div className={`BrandHero__cta BrandHero__cta--${props.ctaJustify}`}>
                    <ButtonWrapper 
                        href={props.ctaLink} 
                        variant={props.buttonVariant}
                        target={'_self'}
                        size={props.ctaSize}>
                        {transformHTMLParserLinks(props.ctaText)}
                    </ButtonWrapper>
                </div>
            </div>
        );
    };

    const SubHeading = useCallback(() => {
        if (!props.subheading || !props.subheading.trim().length) {
            return (<></>);
        }
        const Tag = (props.subheadingTag || 'h2') as keyof JSX.IntrinsicElements;
        return (
            <Tag
                className={`BrandHero__SubHeading ${alignment[props.subheadingAlignment]} ${textClasses}`.trim()}>
                {transformHTMLParserLinks(props.subheading || '')}
            </Tag>);
    }, [
        props,
    ]);

    const textClasses = getTextClasses();

    return (
        <Section {...outerContainerProperties} role='region'>
            <div className={getContentClasses()}>
                {props.headline && (
                    <h1 className={`BrandHero__headline ${alignment[props.headingAlignment]} ${textClasses}`.trim()}>
                        {transformHTMLParserLinks(props.headline)}
                    </h1>
                )}
                <SubHeading />
                {(hasImage || hasVideo) && (
                    <div className='BrandHero__media'>
                        {renderMedia()}
                    </div>
                )}
                {props.description && (
                    <div className={`BrandHero__description ${alignment[props.descriptionAlignment]} ${textClasses}`.trim()}>
                        <p>{transformHTMLParserLinks(props.description)}</p>
                    </div>
                )}
            </div>

            {props.ctaText && renderCTA()}
        </Section>
    );
};

export default BrandHero;
